import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CalendarOutlined,
  CloseOutlined,
  FileTextOutlined,
  PhoneFilled,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Flex, Spin } from "antd";

import { callDetailsCleared, loadCallDetails } from "../../slice";
import { selectCallDetails } from "../../selector";
import AudioPlayer from "../../../../components/AudioPlayer";
import { formatUkDateTime } from "../../../../../utils/datetime";
import type { RecentCall } from "../../types";
import {
  CallDetailField,
  CallDetailGrid,
  CallDetailIconBox,
  CallDetailLabel,
  CallDetailValue,
  CallModal,
  CallModalBody,
  CallModalClose,
  CallModalDivider,
  CallModalHeader,
  CallModalHeaderIcon,
  CallModalSubtitle,
  CallModalTitle,
  CallStatusLabel,
  CallStatusRow,
  CallSummaryBox,
  CallSummaryLabel,
  CallSummaryText,
  StatusPill,
} from "../../style";

interface CallDetailModalProps {
  call: RecentCall | null;
  open: boolean;
  onClose: () => void;
}

function formatCallId(id: string): string {
  return id.startsWith("#") ? id : `#${id}`;
}

export default function CallDetailModal({
  call,
  open,
  onClose,
}: CallDetailModalProps) {
  const dispatch = useDispatch();
  const { data: details, loading } = useSelector(selectCallDetails);

  useEffect(() => {
    if (!open || !call) return;
    dispatch(loadCallDetails({ callId: call.callId }));
    return () => {
      dispatch(callDetailsCleared());
    };
  }, [open, call, dispatch]);

  if (!call) return null;

  const name = details?.patient_name || call.callerName;
  const phone = details?.phone_number ?? call.callerPhone;
  const summary = details?.call_summary ?? call.purpose;
  const success = details
    ? details.status === "Successful"
    : call.status === "Successful";
  const callId = formatCallId(details?.call_id ?? call.callId);
  const recordingUrl = call.recordingUrl;
  const dateTime = details?.date_time
    ? formatUkDateTime(details.date_time, call.date)
    : call.date;

  return (
    <CallModal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={560}
      destroyOnHidden
    >
      <CallModalHeader align="center" gap={12}>
        <CallModalHeaderIcon align="center" justify="center">
          <PhoneFilled />
        </CallModalHeaderIcon>
        <Flex vertical gap={1} flex={1}>
          <CallModalTitle level={4}>Call Details</CallModalTitle>
          <CallModalSubtitle>
            Full information for call {callId}
          </CallModalSubtitle>
        </Flex>
        <CallModalClose
          aria-label="Close"
          icon={<CloseOutlined />}
          onClick={onClose}
        />
      </CallModalHeader>
      <CallModalDivider />
      <Spin spinning={loading}>
        <CallModalBody vertical gap={22}>
          <CallDetailGrid wrap="wrap" gap={24}>
            <CallDetailField align="center" gap={12}>
              <CallDetailIconBox align="center" justify="center">
                <UserOutlined />
              </CallDetailIconBox>
              <Flex vertical gap={1}>
                <CallDetailLabel>Patient Name</CallDetailLabel>
                <CallDetailValue>{name}</CallDetailValue>
              </Flex>
            </CallDetailField>
            <CallDetailField align="center" gap={12}>
              <CallDetailIconBox align="center" justify="center">
                <FileTextOutlined />
              </CallDetailIconBox>
              <Flex vertical gap={1}>
                <CallDetailLabel>Call ID</CallDetailLabel>
                <CallDetailValue>{callId}</CallDetailValue>
              </Flex>
            </CallDetailField>
            <CallDetailField align="center" gap={12}>
              <CallDetailIconBox align="center" justify="center">
                <PhoneOutlined />
              </CallDetailIconBox>
              <Flex vertical gap={1}>
                <CallDetailLabel>Phone Number</CallDetailLabel>
                <CallDetailValue>{phone}</CallDetailValue>
              </Flex>
            </CallDetailField>
            <CallDetailField align="center" gap={12}>
              <CallDetailIconBox align="center" justify="center">
                <CalendarOutlined />
              </CallDetailIconBox>
              <Flex vertical gap={1}>
                <CallDetailLabel>Date &amp; Time</CallDetailLabel>
                <CallDetailValue>{dateTime}</CallDetailValue>
              </Flex>
            </CallDetailField>
          </CallDetailGrid>
          {recordingUrl && <AudioPlayer src={recordingUrl} />}
          <CallSummaryBox vertical gap={8}>
            <CallSummaryLabel>Call Summary</CallSummaryLabel>
            <CallSummaryText>{summary}</CallSummaryText>
          </CallSummaryBox>
          <CallStatusRow align="center" gap={16}>
            <CallStatusLabel>Status</CallStatusLabel>
            <StatusPill $success={success}>
              {success ? "Successful" : "Unsuccessful"}
            </StatusPill>
          </CallStatusRow>
        </CallModalBody>
      </Spin>
    </CallModal>
  );
}
